import type { ImageTransform } from '@numistic/contracts'
import { useTranslation } from '@numistic/i18n'
import { Button } from '@numistic/ui'
import { useRef, useState, type PointerEvent } from 'react'

type Crop = NonNullable<ImageTransform['crop']>

const clamp = (v: number) => Math.min(1, Math.max(0, v))

/** Кадрирование на оригинале: прямоугольник мышью/пальцем, в долях размера (как хранит сервер). */
export function CropEditor({
  src,
  initial,
  onApply,
  onCancel,
}: {
  src: string
  initial: Crop | null
  onApply: (crop: Crop | null) => void
  onCancel: () => void
}) {
  const { t } = useTranslation()
  const box = useRef<HTMLDivElement>(null)
  const [crop, setCrop] = useState<Crop | null>(initial)
  const start = useRef<{ x: number; y: number } | null>(null)

  const point = (e: PointerEvent) => {
    const r = box.current!.getBoundingClientRect()
    return { x: clamp((e.clientX - r.left) / r.width), y: clamp((e.clientY - r.top) / r.height) }
  }
  const onDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId)
    start.current = point(e)
    setCrop({ ...start.current, w: 0, h: 0 })
  }
  const onMove = (e: PointerEvent) => {
    if (!start.current) return
    const p = point(e)
    const s = start.current
    setCrop({
      x: Math.min(s.x, p.x),
      y: Math.min(s.y, p.y),
      w: Math.abs(p.x - s.x),
      h: Math.abs(p.y - s.y),
    })
  }
  const onUp = () => {
    start.current = null
  }
  const valid = crop && crop.w >= 0.02 && crop.h >= 0.02

  return (
    <div className="grid gap-3">
      <p className="text-sm text-muted-foreground">{t('catalog.images.cropHint')}</p>
      <div
        ref={box}
        className="relative mx-auto w-fit touch-none select-none"
        onPointerDown={onDown}
        onPointerMove={onMove}
        onPointerUp={onUp}
      >
        <img src={src} alt="" draggable={false} className="max-h-[60vh] w-auto" />
        {crop ? (
          <div
            className="pointer-events-none absolute border-2 border-primary shadow-[0_0_0_9999px_rgba(0,0,0,0.45)]"
            style={{
              left: `${crop.x * 100}%`,
              top: `${crop.y * 100}%`,
              width: `${crop.w * 100}%`,
              height: `${crop.h * 100}%`,
            }}
          />
        ) : null}
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" disabled={!valid} onClick={() => onApply(crop)}>
          {t('catalog.images.cropApply')}
        </Button>
        <Button type="button" variant="outline" onClick={() => onApply(null)}>
          {t('catalog.images.cropReset')}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  )
}
